import { type ComponentProps, useEffect, useId, useRef, useState } from 'react';

import { css, keyframes } from '@compiled/react';
import { useIntl } from 'react-intl';

import Button from '@atlaskit/button-v25/new';
import AiGenerativeTextSummaryIcon from '@atlaskit/icon/core/ai-generative-text-summary';
import EmojiIcon from '@atlaskit/icon/core/emoji';
import Lozenge from '@atlaskit/lozenge-next/new';
import { token } from '@atlaskit/tokens';

import {
	INSIGHT_CHARS_PER_SECOND,
	INSIGHT_THINKING_MS,
	surveyInsight,
} from '../../jsm-portal-playground-hr-data';
import { jsmPortalPlaygroundHrMessages as messages } from '../../jsm-portal-playground-hr-messages';
import { JsmPlaygroundHeading } from '../jsm-playground-heading';
import { usePrefersReducedMotion } from '../jsm-playground-motion';
import { JsmPlaygroundRovoCard } from '../jsm-playground-rovo-card';

import { ThemeChip } from './survey-theme-chip';

type ButtonIcon = NonNullable<ComponentProps<typeof Button>['iconBefore']>;
type LozengeIcon = NonNullable<ComponentProps<typeof Lozenge>['iconBefore']>;

const InsightsIcon: ButtonIcon = () => <AiGenerativeTextSummaryIcon label="" size="small" />;
const SentimentIcon: LozengeIcon = () => <EmojiIcon label="" size="small" />;

export type SurveyInsightsProps = {
	isOpen: boolean;
	onOpenChange: (isOpen: boolean) => void;
	/** Off when the demo opened it: a scripted open must not move focus or scroll the page. */
	shouldFocusOnOpen?: boolean;
};

export const SURVEY_INSIGHTS_TRIGGER_TEST_ID = 'jsm-hr-survey-insights-trigger';

/**
 * Question 1's Insights button and the Rovo summary it opens beneath itself.
 *
 * The card is positioned with plain CSS rather than `@atlaskit/popup`: the layout is fixed, so there
 * is nothing to measure, and a popper measuring inside the preview's `transform: scale()` would read
 * rendered pixels and misplace it — or flip it up over the navigation.
 */
export const SurveyInsights = ({
	isOpen,
	onOpenChange,
	shouldFocusOnOpen = true,
}: SurveyInsightsProps) => {
	const intl = useIntl();
	const cardId = useId();
	const triggerRef = useRef<HTMLSpanElement>(null);

	const close = () => {
		onOpenChange(false);
		triggerRef.current?.querySelector('button')?.focus();
	};

	return (
		<div css={anchorStyles}>
			<span ref={triggerRef} css={triggerStyles}>
				<Button
					aria-controls={isOpen ? cardId : undefined}
					aria-expanded={isOpen}
					iconBefore={InsightsIcon}
					isSelected={isOpen}
					onClick={() => onOpenChange(!isOpen)}
					testId={SURVEY_INSIGHTS_TRIGGER_TEST_ID}
				>
					{intl.formatMessage(messages.insights)}
				</Button>
			</span>
			{isOpen && (
				<div css={overlayStyles}>
					<SurveyInsightsCard id={cardId} onClose={close} shouldFocusOnOpen={shouldFocusOnOpen} />
				</div>
			)}
		</div>
	);
};

const SurveyInsightsCard = ({
	id,
	onClose,
	shouldFocusOnOpen,
}: {
	id: string;
	onClose: () => void;
	shouldFocusOnOpen: boolean;
}) => {
	const intl = useIntl();
	const [isThinking, setIsThinking] = useState(true);
	const summary = intl.formatMessage(messages.insightSummary);

	useEffect(() => {
		const timer = window.setTimeout(() => setIsThinking(false), INSIGHT_THINKING_MS);
		return () => window.clearTimeout(timer);
	}, []);

	useEffect(() => {
		const closeOnEscape = (event: KeyboardEvent) => {
			if (event.key === 'Escape') {
				onClose();
			}
		};
		document.addEventListener('keydown', closeOnEscape);
		return () => document.removeEventListener('keydown', closeOnEscape);
	}, [onClose]);

	return (
		<JsmPlaygroundRovoCard
			id={id}
			title={intl.formatMessage(messages.insights)}
			closeLabel={intl.formatMessage(messages.closeInsights)}
			onClose={onClose}
			isThinking={isThinking}
			thinkingLabel={intl.formatMessage(messages.rovoThinking)}
			announcement={summary}
			footerNote={intl.formatMessage(messages.usesAi)}
			helpfulLabel={intl.formatMessage(messages.helpful)}
			notHelpfulLabel={intl.formatMessage(messages.notHelpful)}
			shouldFocusOnOpen={shouldFocusOnOpen}
		>
			<SurveyInsightResult summary={summary} />
		</JsmPlaygroundRovoCard>
	);
};

/**
 * Reveals `text` a character at a time, like Rovo streaming its answer. Driven by elapsed time
 * rather than a per-character timer, so a dropped frame can't slow the reveal down.
 */
const useTypewriter = (text: string, shouldType: boolean) => {
	const [count, setCount] = useState(shouldType ? 0 : text.length);

	useEffect(() => {
		if (!shouldType) {
			setCount(text.length);
			return;
		}

		const start = performance.now();
		let frame = 0;
		const tick = (now: number) => {
			const next = Math.min(
				text.length,
				Math.floor(((now - start) / 1000) * INSIGHT_CHARS_PER_SECOND),
			);
			setCount(next);
			if (next < text.length) {
				frame = requestAnimationFrame(tick);
			}
		};
		frame = requestAnimationFrame(tick);
		return () => cancelAnimationFrame(frame);
	}, [text, shouldType]);

	return { visibleText: text.slice(0, count), isDone: count >= text.length };
};

/**
 * The typed summary is hidden from assistive technology: the card announces the whole answer once
 * through its live region instead of it being read out as it types.
 */
const SurveyInsightResult = ({ summary }: { summary: string }) => {
	const intl = useIntl();
	const prefersReducedMotion = usePrefersReducedMotion();
	const { visibleText, isDone } = useTypewriter(summary, !prefersReducedMotion);

	return (
		<div css={resultStyles}>
			{/* Fit to its content: the result column would otherwise stretch it to a full-width bar. */}
			<span css={sentimentStyles}>
				<Lozenge
					appearance="success"
					iconBefore={SentimentIcon}
					trailingMetric={intl.formatMessage(messages.sentimentShare, {
						share: surveyInsight.sentimentShare,
					})}
				>
					{intl.formatMessage(messages.sentimentLabel)}
				</Lozenge>
			</span>
			<p css={summaryStyles} aria-hidden="true">
				{visibleText}
				{!isDone && <span css={caretStyles}>▍</span>}
			</p>
			{/* Only lands once the summary has finished typing, so it never sits half-formed beneath it. */}
			{isDone && (
				<div css={themesStyles}>
					<JsmPlaygroundHeading level={6} size="xxsmall">
						{intl.formatMessage(messages.topThemes)}
					</JsmPlaygroundHeading>
					<ul css={themeListStyles}>
						{surveyInsight.themes.map((theme) => (
							<li key={theme.id} css={themeItemStyles}>
								<ThemeChip>{intl.formatMessage(theme)}</ThemeChip>
							</li>
						))}
					</ul>
				</div>
			)}
		</div>
	);
};

const blink = keyframes({
	'0%, 100%': { opacity: 0 },
	'50%': { opacity: 1 },
});

const themesIn = keyframes({
	from: { opacity: 0, transform: 'translateY(4px)' },
	to: { opacity: 1, transform: 'none' },
});

const anchorStyles = css({
	position: 'relative',
	display: 'inline-flex',
});

const triggerStyles = css({
	display: 'inline-flex',
});

// Hangs off the trigger's trailing edge, above the chart's positioned gridlines and bars. Local to
// the preview card's stacking context, so the playground cursor still paints over it.
const overlayStyles = css({
	position: 'absolute',
	zIndex: 20,
	insetBlockStart: `calc(100% + ${token('space.100')})`,
	insetInlineEnd: 0,
});

const resultStyles = css({
	display: 'flex',
	flexDirection: 'column',
	gap: token('space.150'),
});

const sentimentStyles = css({
	alignSelf: 'flex-start',
});

const summaryStyles = css({
	margin: 0,
	color: token('color.text'),
	font: token('font.body'),
});

const caretStyles = css({
	animation: `${blink} 0.8s step-end infinite`,
});

const themesStyles = css({
	display: 'flex',
	flexDirection: 'column',
	gap: token('space.100'),
	animation: `${themesIn} 260ms cubic-bezier(0.15, 1, 0.3, 1)`,
	'@media (prefers-reduced-motion: reduce)': {
		animation: 'none',
	},
});

const themeListStyles = css({
	display: 'flex',
	flexWrap: 'wrap',
	gap: token('space.075'),
	margin: 0,
	padding: 0,
	listStyle: 'none',
});

const themeItemStyles = css({
	display: 'inline-flex',
	margin: 0,
});
